import * as React from 'react';
// mui
import {
  Box,
  List,
  ListSubheader,
  Divider,
  Button,
} from '@mui/material';
// dayjs
import dayjs, { Dayjs } from 'dayjs';
// types
import RecurringTask from '../../interfaces/RecurringTask.interface';
import Task from '../../interfaces/Task.interface';
import Event from '../../interfaces/Event.interface';
// redux
import { useSelector } from 'react-redux';
import { selectUncompleteTasks } from '../../store/selectors/taskSelectors';
import { selectUncompleteEvents } from '../../store/selectors/eventSelectors';
import { selectUncompleteRecurringTasks } from '../../store/selectors/recuringTaskSelectors';
// components
import EventItem from '../TasksPage/EventItem/EventItem';
import RecurringTaskItem from '../TasksPage/RecurringTaskItem/RecurringTaskItem';
import TaskItem from '../TasksPage/TaskItem/TaskItem';

export default function UpcomingList() {
  // redux
  const localUncompletedTasks = useSelector(selectUncompleteTasks());
  const localUncompletedEvents: Event[] = useSelector(selectUncompleteEvents());
  const localUncompleteRecurringTasks: RecurringTask[] = useSelector(selectUncompleteRecurringTasks());

  // local state
  const [ dates, setDates ] = React.useState<Dayjs[]>([ dayjs().startOf('day') ])

  const addMonth = () => {
    const tempDates = [ ...dates ];
    const datesLength: number = tempDates.length;

    for (let i = (datesLength - 1); i < (datesLength + 31); i += 1) {
      tempDates.push(tempDates[i].add(1, 'day'));
    }

    setDates(tempDates);
  }

  React.useEffect(() => {
    addMonth();
  }, []);

  const renderOverdueTasks = (): React.ReactNode => {
    const currentDate = dayjs().startOf('day').utc();
    return (
      <Box sx={{ mb: 2}}>
        <ListSubheader>Overdue</ListSubheader>
        <Divider />
        { localUncompletedEvents.filter((event: Event) =>
          dayjs.utc(event.startTime).local().startOf('day').utc().isBefore(currentDate)
        ).map((localEvent: Event) => (
          <EventItem
            key={localEvent.id}
            padding={2}
            parentTaskId={localEvent.parentTaskId}
            eventId={localEvent.id}
            showProject
          />
        ))}

        { localUncompleteRecurringTasks.filter((task: RecurringTask) =>
          dayjs.utc(task.dueDate).local().startOf('day').utc().isBefore(currentDate)
        ).map((localTask: RecurringTask) => (
          <RecurringTaskItem
            key={localTask.id}
            padding={2}
            parentTaskId={localTask.parentTaskId}
            recurringTaskId={localTask.id}
            showProject
          />
        ))}

        { localUncompletedTasks.filter((task: Task) =>
          dayjs.utc(task.dueDate).local().startOf('day').utc().isBefore(currentDate)
        ).map((localTask: Task) => (
          <TaskItem
            key={localTask.id}
            padding={2}
            parentTaskId={localTask.parentTaskId}
            taskId={localTask.id}
            showProject
          />
        ))}
      </Box>
    )
  }

  const renderUpcomingTasks = (): React.ReactNode => {
    return dates.map((date, index) => (
      <Box key={index} sx={{ mb: 2 }}>
        <ListSubheader>{
          date.startOf('year').isSame(dayjs().startOf('year'))
          ? date.format('dddd, MMM D')
          : date.format('dddd, MMM D, YYYY')
        }</ListSubheader>
        <Divider />

        { localUncompletedEvents.filter((event: Event) =>
          dayjs.utc(event.startTime).local().startOf('day').utc().isSame(dayjs(date).utc())
        ).map((localEvent: Event) => (
          <EventItem
            key={localEvent.id}
            padding={2}
            parentTaskId={localEvent.parentTaskId}
            eventId={localEvent.id}
            showProject
          />
        ))}

        { localUncompleteRecurringTasks.filter((task: RecurringTask) =>
          dayjs.utc(task.dueDate).isSame(dayjs(date).utc())
        ).map((localTask: RecurringTask) => (
          <RecurringTaskItem
            key={localTask.id}
            padding={2}
            parentTaskId={localTask.parentTaskId}
            recurringTaskId={localTask.id}
            showProject
          />
        ))}

        { localUncompletedTasks.filter((task: Task) =>
          dayjs.utc(task.dueDate).isSame(dayjs(date).utc())
        ).map((localTask: Task) => (
          <TaskItem
            key={localTask.id}
            padding={2}
            parentTaskId={localTask.parentTaskId}
            taskId={localTask.id}
            showProject
          />
        ))}
      </Box>
    ));
  };

  return (
    <List disablePadding>
      {renderOverdueTasks()}
      {renderUpcomingTasks()}
      <Button
        sx={{ mt: 2 }}
        fullWidth
        variant='outlined'
        onClick={addMonth}
      >View More</Button>
    </List>
  );
}
